<script lang="ts">
  import { browser } from '$app/environment';

  import { afterUpdate, tick } from 'svelte';

  import { CommonDto } from '@do/data-transfer-objects';
  import { DirectusImage, Preset } from '@do/svelte-directus-images';

  import { getProductWrapperMaxWidthBasedOnArtworkHeight } from '@shared/ui/list';
  import type { OriginalSize } from '@shared/ui/list/types';
  import { MediaPlayer, mediaPlayerConfigs } from '@shared/ui/media-player';
  import { onImageOnloadHandler } from '@shared/ui/transition-animator/helpers';
  import { cn } from '@shared/utils';

  import { ArtAssetCardApi, TILES_FORMAT, type ArtAsset } from '@entities/art-asset';

  // import IconScaleAnimatedButton from './scale-button.svelte';
  import ViewButton from './view-button.svelte';

  export let alt: string;

  export let previewImage: CommonDto.Photo | null;
  export let isOnlyPreviewShow = false;
  export let currentSrc: string | undefined = undefined;
  export let isImageBlurred = false;
  export let digitalCommon: ArtAsset['digital_common'] | undefined = undefined;

  let maxWidth = 'initial';
  let isLoaded = false;

  export let imgElement: HTMLImageElement;
  export let videoElement: HTMLVideoElement | undefined = undefined;

  const handleImageLoaded = () => {
    isLoaded = true;

    currentSrc = imgElement.currentSrc;

    if (imgElement.naturalWidth > 0) {
      const size: OriginalSize = {
        w: imgElement.naturalWidth,
        h: imgElement.naturalHeight,
      };

      maxWidth = getProductWrapperMaxWidthBasedOnArtworkHeight(size, undefined, 95);
    }

    tick().then(() => onImageOnloadHandler(imgElement));
  };

  function initialCalculations() {
    if (imgElement) {
      if (imgElement.complete) {
        handleImageLoaded();
      } else {
        imgElement.onload = handleImageLoaded;
      }
    }
  }

  afterUpdate(() => {
    initialCalculations();
  });

  const artAssetCardApi = new ArtAssetCardApi(fetch);
  let fullScreenBtnVisible = false;

  async function updateTilesData(id: string | number | undefined) {
    fullScreenBtnVisible = false;
    if (typeof id !== 'string') return;
    const tilesDataRes = await artAssetCardApi.readTilesData(id);
    //show button only when tiles are available in viewer's format (for now its webp only)
    fullScreenBtnVisible = !!tilesDataRes?.find((item) => item.format === TILES_FORMAT);
  }

  function isViewerCompatible(type: string | null | undefined) {
    return Boolean(type && mediaPlayerConfigs.IMAGE_FORMATS.includes(type.toLowerCase()));
  }

  $: if (browser) {
    updateTilesData(previewImage?.id);
  }
</script>

<div
  class="{cn('relative mx-auto h-max w-full transition-opacity', {
    'opacity-100': isLoaded,
    'opacity-0': !isLoaded && !digitalCommon,
    'cursor-pointer': fullScreenBtnVisible && !digitalCommon,
    'cursor-default': !fullScreenBtnVisible,
    'blur-xl': isImageBlurred,
  })}"
  style="max-width: {maxWidth};"
>
  {#if isOnlyPreviewShow && digitalCommon}
    {@const displayViewButton = isViewerCompatible(digitalCommon.type)}

    <button class="group w-full" on:click disabled="{!fullScreenBtnVisible || !displayViewButton}">
      <div class="mb-4">
        <DirectusImage
          imageData="{previewImage}"
          imgClasses="w-full"
          {alt}
          pictureSizes="{{
            default: '100vw',
            'min-1024': '60vw',
            'min-1280': '67vw',
          }}"
          presetKeys="{Preset.Thumbnail}"
          bind:imgElement
          loading="lazy"
          onErrorPlaceholderStatic="{{
            src: '/images/artwork-placeholder.png',
            width: 1000,
            height: 1000,
          }}"
        />
      </div>

      {#if fullScreenBtnVisible && displayViewButton}
        <ViewButton />
      {/if}
    </button>
  {:else if digitalCommon && digitalCommon.filename_disk}
    <div class="flex justify-center">
      <MediaPlayer
        filename="{digitalCommon.filename_disk}"
        type="{digitalCommon.type}"
        title="{alt}"
        {previewImage}
        bind:imgElement
        bind:videoElement
        bind:isLoaded
        on:click
        on:onImageLoad="{initialCalculations}"
      />
    </div>

    {#if isViewerCompatible(digitalCommon.type) && fullScreenBtnVisible}
      <button class="mx-auto mt-4 block" on:click>
        <ViewButton />
      </button>
    {/if}
  {:else}
    <button class="group w-full" on:click disabled="{!fullScreenBtnVisible}">
      <div class="mb-4">
        <DirectusImage
          imageData="{previewImage}"
          imgClasses="w-full"
          {alt}
          pictureSizes="{{
            default: '100vw',
            'min-1024': '60vw',
            'min-1280': '67vw',
          }}"
          presetKeys="{Preset.Thumbnail}"
          bind:imgElement
          loading="lazy"
          onErrorPlaceholderStatic="{{
            src: '/images/artwork-placeholder.png',
            width: 1000,
            height: 1000,
          }}"
        />
      </div>

      {#if fullScreenBtnVisible}
        <ViewButton />
      {/if}
    </button>
  {/if}
</div>
